import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ses from "../../assets/SES Logo.png";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/mhada-properties", label: "Mhada Properties" },
  { to: "/security", label: "Security" },
  { to: "/about", label: "About Us" },
];

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 h-20 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-2xl shadow-lg border-b border-slate-200"
            : "bg-white/90 backdrop-blur-xl border-b border-white/60"
        }`}
      >
        {/* Soft brand gradient wash */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#00AEEF]/5 via-[#EC008C]/4 to-[#ED1C24]/5 pointer-events-none" />
        {/* Animated brand gradient accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] ses-brand-gradient opacity-70" />

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
          {/* Logo & Branding */}
          <Link
            to="/"
            className="group flex items-center gap-2.5 sm:gap-4 shrink-0 min-w-0"
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative">
                <div className="absolute -inset-1 rounded-2xl ses-brand-gradient opacity-25 blur-sm" />
                <div className="relative w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-white p-1 logo-shield-glow border border-slate-100 transition-transform duration-300 group-hover:scale-105 group-hover:rotate-1 shadow-sm">
                  <img
                    src={ses}
                    alt="SES"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col min-w-0 justify-center">
              <span className="text-xs sm:text-sm md:text-base font-bold text-slate-900 leading-tight truncate sm:whitespace-normal">
                Sai Enforcement And Security Pvt Ltd
              </span>
              <span className="inline-flex items-center gap-1.5 mt-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ses-cyan opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-ses-cyan" />
                </span>
                <span className="text-[8px] sm:text-[10px] font-semibold uppercase tracking-[0.15em] ses-brand-text">
                  Authorized MHADA Agency
                </span>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            <nav className="flex items-center gap-1 lg:gap-2">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link-underline relative px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                    isActive(link.to)
                      ? "text-slate-900 active"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <Link
              to="/contact"
              className="ml-3 lg:ml-4 relative overflow-hidden group px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,174,239,0.35)] hover:-translate-y-0.5 active:translate-y-0"
            >
              <span className="absolute inset-0 ses-brand-gradient opacity-90 group-hover:opacity-100 transition-opacity" />
              <span className="relative flex items-center gap-2">
                Call Now
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform group-hover:translate-x-0.5"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </span>
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden relative z-[60] w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 transition-colors"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <div className="w-5 h-4 relative flex flex-col justify-between">
              <span
                className={`block h-0.5 w-full bg-slate-700 rounded-full transition-all duration-300 origin-center ${
                  isMobileMenuOpen ? "translate-y-[7px] rotate-45" : ""
                }`}
              />
              <span
                className={`block h-0.5 w-full bg-slate-700 rounded-full transition-all duration-300 ${
                  isMobileMenuOpen ? "opacity-0 scale-0" : ""
                }`}
              />
              <span
                className={`block h-0.5 w-full bg-slate-700 rounded-full transition-all duration-300 origin-center ${
                  isMobileMenuOpen ? "-translate-y-[7px] -rotate-45" : ""
                }`}
              />
            </div>
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[55] mobile-nav-enter">
          <div
            className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          <div className="absolute top-0 right-0 bottom-0 w-full max-w-sm bg-gradient-to-b from-white via-slate-50 to-white border-l border-slate-200 shadow-2xl flex flex-col pt-24 px-6 pb-8">
            <div className="h-1 w-16 ses-brand-gradient rounded-full mb-8" />

            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((link, i) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`mobile-link-stagger flex items-center justify-between px-4 py-4 rounded-2xl text-lg font-semibold transition-all duration-300 ${
                    isActive(link.to)
                      ? "bg-white text-slate-900 border border-slate-200 shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/80"
                  }`}
                  style={{ animationDelay: `${i * 0.07 + 0.1}s` }}
                >
                  {link.label}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-40"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </Link>
              ))}
            </nav>

            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mobile-link-stagger mt-6 relative overflow-hidden rounded-2xl py-4 text-center text-base font-bold text-white shadow-md"
              style={{ animationDelay: "0.4s" }}
            >
              <span className="absolute inset-0 ses-brand-gradient" />
              <span className="relative">Get Started Today</span>
            </Link>

            <div className="mt-auto pt-8 border-t border-slate-200">
              <div className="flex items-center gap-3">
                <img
                  src={ses}
                  alt="SES"
                  className="w-10 h-10 object-contain bg-white rounded-xl p-0.5 border border-slate-100 shadow-sm"
                />
                <div>
                  <p className="text-xs font-bold text-slate-900">
                    SES Pvt Ltd
                  </p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                    Security & Enforcement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
